import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Star, ArrowRight, ArrowUpRight } from "lucide-react";
import { goToAppStore, api } from "../lib/api";

export const Hero = ({ onLoginClick }) => {
  const [appstore, setAppstore] = useState(null);

  useEffect(() => {
    api
      .get("/reviews")
      .then((r) => setAppstore(r.data?.ratings?.appstore || null))
      .catch(() => {});
  }, []);

  return (
    <section id="top" className="relative pt-32 pb-16 md:pt-44 md:pb-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-6">
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="eyebrow"
            >
              The Precision Hair Method
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.05 }}
              className="mt-5 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stylist-ink"
            >
              Stop Guessing Your Routine.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.12 }}
              className="mt-5 font-serif text-xl md:text-2xl text-stylist-ink leading-snug max-w-xl"
            >
              Vetted senior stylists, precision hair science,{" "}
              <span className="italic text-stylist-gold">verified results in 30 days.</span>
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="mt-6 text-base md:text-lg text-stylist-ink2 max-w-xl leading-relaxed"
            >
              Most hair routines fail because they ignore your environment and your hair's unique
              biological history. We combine local environmental data with veteran salon expertise
              to diagnose, recommend, and scientifically verify your hair's transformation over 30 days.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
            >
              <button
                onClick={goToAppStore}
                data-testid="hero-cta-book"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-stylist-ink text-white px-9 py-4 text-base font-medium shadow-lg shadow-stylist-ink/10 hover:bg-stylist-gold hover:text-stylist-ink transition-colors duration-300"
              >
                Start Your Diagnosis
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <Link
                to="/how-it-works"
                data-testid="hero-cta-method"
                className="group inline-flex items-center gap-1.5 self-center text-sm text-stylist-muted hover:text-stylist-ink ss-link-underline transition-colors"
              >
                Explore The Precision Method
                <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="mt-10 flex items-center gap-4"
            >
              <div className="flex -space-x-3">
                {[
                  "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&q=80",
                  "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=120&q=80",
                  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&q=80",
                ].map((src) => (
                  <img
                    key={src}
                    src={src}
                    alt="Client"
                    className="w-10 h-10 rounded-full border-2 border-[#FAF8F5] object-cover"
                  />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-stylist-gold text-stylist-gold" />
                  ))}
                </div>
                <p className="text-xs text-stylist-muted mt-1 font-mono tracking-wide" data-testid="hero-appstore-rating">
                  {appstore
                    ? `Rated ${appstore.score}/5 on the App Store`
                    : "Rated on the App Store"}
                </p>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-6 relative"
          >
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1605497788044-5a32c7078486?w=1600&q=80"
                alt="Editorial hair salon"
                className="w-full h-[420px] md:h-[560px] object-cover rounded-lg"
              />
              <div className="absolute -bottom-6 -left-4 md:left-8 bg-white rounded-lg shadow-xl border border-stylist-border p-5 max-w-[240px]">
                <p className="eyebrow">Style DNA Matrix</p>
                <p className="font-serif text-2xl text-stylist-ink mt-1 leading-tight">
                  15-question hair intake
                </p>
                <p className="text-xs text-stylist-ink2 mt-2">
                  Paired to a certified senior stylist in minutes.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
